import React, { useState, useEffect, useCallback } from 'react';
import { Upload } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import {
  Form,
  Input,
  Button,
  Modal,
  Select,
  DatePicker,
  Row,
  Col
} from 'antd';
import ConfirmModal from '../ConfirmModal';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

// Подключаем UTC плагин
dayjs.extend(utc);

const { Option } = Select;
const { TextArea } = Input;

const CreateModal = ({
  visible,
  onCancel,
  onSubmit,
  initialValues,
  candidates,
  users,
  vacancies
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [candidateName, setCandidateName] = useState('');
  const [requireResult, setRequireResult] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [selectedCandidateId, setSelectedCandidateId] = useState(null);
  const [candidateVacancies, setCandidateVacancies] = useState([]);

  const loadCandidateData = React.useCallback(async (candidateId, organisationId) => {
    try {
      const candidate = candidates.find(c => c.id === candidateId);
      console.log("candidate", candidate);
      if (candidate) {
        setCandidateName(candidate.full_name);
        setSelectedCandidateId(candidateId);
        form.setFieldsValue({ candidate_name: candidate.full_name });

        // Получаем вакансии, связанные с выбранным кандидатом
        const candidateVacancies = candidate.vacancies || [];
        setCandidateVacancies(candidateVacancies);

        // Сбрасываем выбранную вакансию при смене кандидата
        form.setFieldsValue({ vacancy_id: undefined });
      }
    } catch (error) {
      console.error('Ошибка загрузки данных кандидата:', error);
    }
  }, [candidates, form]);

  useEffect(() => {
    if (visible) {
      if (initialValues) {
        form.setFieldsValue({
          candidate_id: initialValues.candidate_id,
        });

        if (initialValues.candidate_id) {
          loadCandidateData(initialValues.candidate_id, initialValues?.organisation_id);
        }
      } else {
        form.resetFields();
        setCandidateName('');
        setRequireResult(false);
        setSelectedCandidateId(null);
        setCandidateVacancies([]);
      }
      setHasUnsavedChanges(false);
    }
  }, [visible, initialValues, form, loadCandidateData]);

  const handleCandidateChange = (candidateId) => {
    loadCandidateData(candidateId, initialValues?.organisation_id);
  };

  const handleVacancyChange = (vacancyId) => {
    console.log('Selected vacancy:', vacancyId);
  };

  const checkForChanges = useCallback(() => {
    const currentValues = form.getFieldsValue(true);
    const hasValues = Object.values(currentValues).some(value => {
      if (value === null || value === undefined) return false;
      if (typeof value === 'string') return value.trim() !== '';
      if (Array.isArray(value)) return value.length > 0;
      return true;
    });
    return hasValues;
  }, [form]);

  const handleFormChange = useCallback(() => {
    setHasUnsavedChanges(checkForChanges());
  }, [checkForChanges]);

  const handleCancel = useCallback(() => {
    if (hasUnsavedChanges) {
      setShowConfirm(true);
    } else {
      onCancel();
    }
  }, [hasUnsavedChanges, onCancel]);

  const handleConfirmClose = useCallback((shouldClose) => {
    if (shouldClose) {
      onCancel();
    }
    setShowConfirm(false);
  }, [onCancel]);

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();

      const taskData = {
        ...values,
        ...initialValues,
        // Используем локальную дату без преобразования в UTC
        deadline: values.due_date ? `${values.due_date.format('YYYY-MM-DD')}T00:00:00.000Z` : null,
        result_required: requireResult,
        creator_id: parseInt(localStorage.getItem('user_id')),
      };

      if (values.co_assignee_id) {
        taskData.co_assignee_id = parseInt(values.co_assignee_id);
      }
      if (values.observer_id) {
        taskData.observer_id = parseInt(values.observer_id);
      }
      if (values.assignee_id) {
        taskData.assignee_id = parseInt(values.assignee_id);
      }
      if (values.candidate_id) {
        taskData.candidate_id = parseInt(values.candidate_id);
      }
      if (values.vacancy_id) {
        taskData.vacancy_id = parseInt(values.vacancy_id);
      }

      await onSubmit(taskData);
      setHasUnsavedChanges(false);
      onCancel();
    } catch (error) {
      if (error?.errorFields?.length > 0) {
        form.scrollToField(error.errorFields[0].name, {
          behavior: 'smooth',
          block: 'center',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const footerButtons = [
    <Button key="cancel" onClick={handleCancel}>
      Закрыть
    </Button>,
    <Button
      key="submit"
      type="primary"
      loading={loading}
      onClick={handleSubmit}
    >
      Создать задачу
    </Button>
  ];

  return (
    <>
      <Modal
        title="Создать задачу"
        open={visible}
        onCancel={handleCancel}
        width={1000}
        footer={footerButtons}
      >
        <Form form={form} layout="vertical" onFieldsChange={handleFormChange}>
          <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
              Основная информация
            </h3>

            {initialValues?.candidate_id ? (
              <Form.Item name="candidate_name" label="Кандидат">
                <Input disabled value={candidateName} />
                <input type="hidden" name="candidate_id" value={initialValues.candidate_id} />
              </Form.Item>
            ) : (
              <Form.Item name="candidate_id" label="Кандидат" rules={[{ required: true, message: 'Выберите кандидата!' }]}>
                <Select
                  showSearch
                  optionFilterProp="children"
                  onChange={handleCandidateChange}
                  placeholder="Выберите кандидата"
                >
                  {candidates.map(candidate => (
                    <Option key={candidate.id} value={candidate.id}>
                      {candidate.full_name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            )}

            <Form.Item
              name="vacancy_id"
              label="Вакансия"
              rules={[{ required: true, message: 'Выберите вакансию!' }]}
            >
              <Select
                placeholder={selectedCandidateId ? "Выберите вакансию" : "Сначала выберите кандидата"}
                onChange={handleVacancyChange}
                disabled={!selectedCandidateId || candidateVacancies.length === 0}
              >
                {candidateVacancies.map(vacancy => (
                  <Option key={vacancy.id} value={vacancy.id}>
                    {vacancy.job_title}
                  </Option>
                ))}
              </Select>
              {selectedCandidateId && candidateVacancies.length === 0 && (
                <div style={{ color: '#ff4d4f', fontSize: '12px', marginTop: '4px' }}>
                  У выбранного кандидата нет связанных вакансий
                </div>
              )}
            </Form.Item>

            <Form.Item
              name="description"
              label="Описание"
              rules={[{ required: true, message: 'Введите описание задачи!' }]}
            >
              <TextArea rows={4} />
            </Form.Item>

            <Form.Item
              name="due_date"
              label="Срок выполнения"
              rules={[{ required: true, message: 'Выберите срок выполнения!' }]}
            >
              <DatePicker
                style={{ width: '100%' }}
                format="DD.MM.YYYY"
              />
            </Form.Item>
          </div>

          <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
              Материалы
            </h3>

            <Form.Item name="materials" label="Прикрепленные файлы" valuePropName="fileList" getValueFromEvent={e => Array.isArray(e) ? e : e?.fileList}>
              <Upload
                name="file"
                beforeUpload={() => false}
                multiple
              >
                <Button icon={<UploadOutlined />}>Прикрепить файл</Button>
              </Upload>
            </Form.Item>
          </div>

          <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
              Ответственные лица
            </h3>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="assignee_id" label="Исполнитель" rules={[{ required: true, message: 'Выберите исполнителя!' }]}>
                  <Select showSearch optionFilterProp="children" placeholder="Выберите исполнителя">
                    {users.map(user => (
                      <Option key={user.id} value={user.id}>
                        {user.first_name} {user.last_name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="co_assignee_id" label="Соисполнитель">
                  <Select showSearch optionFilterProp="children" placeholder="Выберите соисполнителя">
                    {users.map(user => (
                      <Option key={user.id} value={user.id}>
                        {user.first_name} {user.last_name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Form.Item name="observer_id" label="Наблюдатель">
              <Select showSearch optionFilterProp="children" placeholder="Выберите наблюдателя">
                {users.map(user => (
                  <Option key={user.id} value={user.id}>
                    {user.first_name} {user.last_name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </div>

          <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
              Дополнительные настройки
            </h3>

            <Form.Item name="result_required" label="Не завершать без результата">
              <Button
                type={requireResult ? "primary" : "default"}
                onClick={() => setRequireResult(!requireResult)}
                style={{
                  width: '100%',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: requireResult ? 'bold' : 'normal'
                }}
              >
                {requireResult ? '✓ Не завершать без результата' : 'Не завершать без результата'}
              </Button>
            </Form.Item>
          </div>
        </Form>
      </Modal>

      <ConfirmModal
        visible={showConfirm}
        onConfirm={handleConfirmClose}
        onCancel={() => setShowConfirm(false)}
        title="Подтверждение закрытия"
        message="Данные не будут сохранены. Продолжить?"
        okText="Продолжить"
        cancelText="Отмена"
        type="warning"
      />
    </>
  );
};

export default CreateModal;